import { useEffect, useState } from 'react';
import { ArrowRight, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { Button, ConfirmDialog, EmptyState, PageHeader, Spinner } from '../components/ui';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { eventBuilderService, type EventBuilderDraftSummary } from '../services/eventBuilderService';

function CreateLink() {
  return <Link to="/ai-event-builder/new" className="inline-flex items-center gap-2 rounded-lg border border-transparent bg-sky-500 px-4 py-2 text-sm font-medium text-white hover:bg-sky-400">
    <Plus size={14} /> Create with AI
  </Link>;
}

export default function AIEventBuilderLanding() {
  const { activeWorkspaceId, canCreateEvents } = useAdminAuth();
  const [drafts, setDrafts] = useState<EventBuilderDraftSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventBuilderDraftSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    if (!activeWorkspaceId) {
      setDrafts([]);
      return () => { active = false; };
    }
    setLoading(true);
    setError(null);
    eventBuilderService.list(activeWorkspaceId)
      .then((items) => { if (active) setDrafts(items); })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : 'Unable to load event drafts');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [activeWorkspaceId]);

  async function removeDraft() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await eventBuilderService.remove(deleteTarget.id);
      setDrafts((current) => current.filter((draft) => draft.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (reason) {
      setDeleteError(reason instanceof Error ? reason.message : 'Unable to delete event draft');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  const activeDrafts = drafts.filter((draft) => draft.status === 'active' && !draft.created_event_id);

  return <div className="p-8">
    <PageHeader
      title="AI Event Builder"
      description="Describe your event, review the attendee experience, then create a normal editable event draft."
      action={activeWorkspaceId && canCreateEvents ? <CreateLink /> : undefined}
    />
    {!activeWorkspaceId ? (
      <EmptyState icon={Sparkles} title="Choose an approved workspace" description="Event drafts are saved inside the active organizer workspace." />
    ) : loading ? (
      <div className="flex justify-center py-20"><Spinner className="h-6 w-6 text-gray-500" /></div>
    ) : error ? (
      <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">{error}</p>
    ) : activeDrafts.length === 0 ? (
      <EmptyState icon={Sparkles} title="No AI event drafts yet" description="Start with a short description; the builder will ask only for missing decisions." action={<CreateLink />} />
    ) : (
      <>
        {deleteError && <p role="alert" className="mb-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">{deleteError}</p>}
        <div className="space-y-3">
          {activeDrafts.map((draft) => {
            const title = draft.requirements.name || 'Untitled event';
            return <div key={draft.id} className="flex items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 p-5">
              <div className="min-w-0">
                <p className="truncate font-medium text-white">{title}</p>
                <p className="mt-1 text-xs text-gray-500">{draft.message_count} conversation messages · Revision {draft.revision}</p>
                {draft.latest_assistant_summary && <p className="mt-2 line-clamp-1 text-sm text-gray-400">{draft.latest_assistant_summary}</p>}
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <Link aria-label={`Resume ${title}`} to={`/ai-event-builder/${draft.id}`} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-sky-400 hover:bg-sky-500/10">Resume <ArrowRight size={14} /></Link>
                <Button aria-label={`Delete ${title}`} variant="danger" size="sm" icon={<Trash2 size={13} />} onClick={() => setDeleteTarget(draft)}>Delete</Button>
              </div>
            </div>;
          })}
        </div>
      </>
    )}
    <ConfirmDialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} onConfirm={() => void removeDraft()} loading={deleting} title="Delete AI event draft?" description={`Delete "${deleteTarget?.requirements.name || 'Untitled event'}"? This cannot be undone.`} confirmLabel="Delete draft" />
  </div>;
}
